import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { ScopedRegisterEmptyState } from '@client/components/_shared/ScopedRegisterEmptyState';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { User } from '@client/components/_ui/display/User';
import { Space } from '@client/components/_ui/layout/Space';
import { ClaimClientUpdateIndicator } from '@client/components/claims/ClaimClientUpdateIndicator';
import { ClaimDecisionBadge } from '@client/components/claims/ClaimDecisionBadge';
import {
    type ClaimReassignmentAction,
    ClaimReassignmentDrawer,
} from '@client/components/claims/ClaimReassignmentDrawer';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { NewClaimMenu } from '@client/components/claims/NewClaimMenu';
import { ContactPopover } from '@client/components/contacts/ContactPopover';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import {
    builtInViewPayload,
    TableViewsBar,
    useEntityBuiltInViews,
    useTableViews,
} from '@client/components/table-views';
import { ICONS } from '@client/constants/icons';
import { useClaimDeferredBindingEnabled } from '@client/hooks/business/claim/useClaimDeferredBinding';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import {
    CONFIG_LOOKUP_QUERY_OPTIONS,
    LIST_ROUTE_PREFETCH_OPTIONS,
    LIST_ROUTE_QUERY_OPTIONS,
} from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, ORG_ROLES } from '@shared/constants';
import { CLAIM_SOURCE_LABELS } from '@shared/constants/business/claim-product-binding';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { isClaimAwaitingProduct } from '@shared/utils/business/claim-product-model';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import type { Key } from 'antd/es/table/interface';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { runDetailRoutePrefetchWhenAuthorised, shouldPrefetchDetailRouteData } from '../detail-route-prefetch';
import { freshClaimsListInput, freshClaimsListInputWithWhere, LIST_TABLE_PERSIST_KEYS } from '../fresh-list-inputs';
import { runListRoutePrefetch } from '../list-route-prefetch';
import {
    CLAIM_DEFERRED_BINDING_FILTER_IDS,
    CLAIM_PRODUCT_FILTER_ID,
    useClaimFilterConfig,
} from './useClaimFilterConfig';

/** The triage queue of claims recorded before their product was known (CUR-2163). */
const AWAITING_PRODUCT_VIEW_ID = '__awaiting-product__';

export const Route = createFileRoute('/console/$orgId/claims/')({
    staticData: {
        metadata: { title: 'Claims' },
        ui: {
            title: 'Claims',
            subtitle: 'Search and manage claims',
            icon: ICONS.claim,
        },
    },
    validateSearch: zodValidator(
        z.object({
            'claim-filters': z
                .array(
                    z.union([
                        z.object({
                            id: z.string(),
                            filterId: z.literal('owner-ids'),
                            operator: FilterOperatorSchema,
                            value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                        }),
                        z.object({
                            id: z.string(),
                            filterId: z.string(),
                            operator: FilterOperatorSchema,
                            value: z.any(),
                        }),
                    ]),
                )
                .optional()
                .catch(undefined),
            view: z.string().optional().catch(undefined),
        }),
    ),
    beforeLoad: ({ context, params, search, location, preload, abortController }): ShareableRouteContext => {
        // Start the saved-views round trip (and, when the initial input is exactly
        // computable, the list query itself) while the auth gate is still rendering.
        // Without this the chain is strictly serial after mount:
        // listSavedViews → viewsReady → listClaims (CUR-1036, task 2.2). Deferred
        // until the session restores on cold deep links, same as the detail routes.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'claim',
                });
            },
            () => {
                runListRoutePrefetch<RouterInputs['claim']['listClaims'], 'CLAIM'>({
                    savedViewsUtils: context.trpcUtils.savedView.listSavedViews,
                    entityType: 'CLAIM',
                    pathname: location.pathname,
                    urlFilterCount: search['claim-filters']?.length ?? 0,
                    urlViewId: search.view,
                    defaultInput: freshClaimsListInput(),
                    prefetchList: (input) => {
                        void context.trpcUtils.claim.listClaims.prefetch(input, LIST_ROUTE_PREFETCH_OPTIONS);
                    },
                });
                // The client-update column config gates the table's loading overlay —
                // warm it alongside the list so neither serialises behind the other.
                void context.trpcUtils.configurationItem.getConfigurationItem.prefetch(
                    { name: 'CLAIM_CLIENT_UPDATE_CONFIGURATION' },
                    { ...CONFIG_LOOKUP_QUERY_OPTIONS, meta: { errorSurface: 'silent', captureToSentry: false } },
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: ClaimSearch,
});

type QueryParams = RouterInputs['claim']['listClaims'];

const columnHelper = createColumnHelper<RouterOutputs['claim']['listClaims']['rows'][number], QueryParams['orderBy']>();

function ClaimSearch() {
    const orgId = useOrgId();
    const searchParams = Route.useSearch();
    const claimClientUpdateConfigurationQuery = useConfigServiceLoader(async (configService) =>
        configService.fetchClaimClientUpdateConfiguration({
            // Shares the freshness window with the `beforeLoad` prefetch so the warm
            // cache is honoured instead of refetching on every mount (CUR-1036).
            staleTime: CONFIG_LOOKUP_QUERY_OPTIONS.staleTime,
        }),
    );
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [reassignmentAction, setReassignmentAction] = usePathState<ClaimReassignmentAction>('reassignment-action', [
        'none',
    ]);
    const { config: filterConfig, ready: filterConfigReady } = useClaimFilterConfig();
    const deferredBindingEnabled = useClaimDeferredBindingEnabled();

    const [queryParams, setQueryParams] = usePathState<QueryParams>(
        'query-params',
        freshClaimsListInputWithWhere(buildInitialWhereFromFilters(filterConfig, searchParams['claim-filters'])),
    );

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const listClaimsQuery = trpc.claim.listClaims.useQuery(queryParams, {
        enabled: viewsReady,
        ...LIST_ROUTE_QUERY_OPTIONS,
    });

    // LCP guardrail (CUR-1036, task 0.1): mark when real rows (or the genuine empty
    // state) commit — static chrome painting earlier must not move this.
    useContentReadyMark(listClaimsQuery.isSuccess);

    const tableProps = usePrismaTable({
        data: listClaimsQuery.data?.rows || [],
        total: listClaimsQuery.data?.total || 0,
        loading: !viewsReady || listClaimsQuery.isLoading || claimClientUpdateConfigurationQuery.isLoading,
        columns: [
            columnHelper.accessor('generatedId', {
                id: 'claimNo',
                header: 'Claim#',
                sortable: 'generatedId',
                cell({ row }) {
                    return row.generatedId || row.id;
                },
                fixed: 'left',
                hideable: false,
                className: 'font-bold whitespace-nowrap',
            }),
            columnHelper.display({
                id: 'clientUpdate',
                header: claimClientUpdateConfigurationQuery.data?.headerWidgetLabel || 'Client update',
                cell({ row }) {
                    return (
                        <ClaimClientUpdateIndicator
                            variant="compact"
                            summary={row.clientUpdateSummary}
                            data={{
                                claimId: row.id,
                                Status: row.Status,
                            }}
                        />
                    );
                },
                hideable: false,
                className: claimClientUpdateConfigurationQuery.data?.isEnabled ? undefined : 'hidden',
            }),
            columnHelper.accessor((row) => row.Owner?.userId, {
                id: 'owner',
                header: 'Owner',
                sortable: 'Owner.firstName',
                cell({ row }) {
                    return (
                        <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.photoUrl} />
                    );
                },
            }),
            columnHelper.accessor('Status', {
                id: 'status',
                header: 'Status',
                sortable: 'Status.name',
                cell({ row }) {
                    return <ClaimStatusTag data={row.Status} />;
                },
                className: 'whitespace-nowrap',
            }),
            // Deferred product binding (CUR-2163): the claim's product, or its state while it awaits one.
            ...(deferredBindingEnabled
                ? [
                      columnHelper.display({
                          id: 'product',
                          header: 'Product',
                          cell({ row }) {
                              if (isClaimAwaitingProduct(row)) {
                                  // Closed without one (CUR-2167): it no longer waits for a product.
                                  return row.Status.type === 'CLOSED' ? (
                                      '-'
                                  ) : (
                                      <StatusBadge module="claimProductBinding" status="PENDING" />
                                  );
                              }
                              return row.ProductVersion?.Product.name ?? row.ProductType?.name ?? '-';
                          },
                          className: 'whitespace-nowrap',
                      }),
                      columnHelper.display({
                          id: 'source',
                          header: 'Source',
                          cell({ row }) {
                              return CLAIM_SOURCE_LABELS[row.source];
                          },
                          className: 'whitespace-nowrap',
                      }),
                      // Why a closed claim closed (CUR-2167); nothing for an open one or one closed without an outcome.
                      columnHelper.display({
                          id: 'closure-outcome',
                          header: 'Closed as',
                          cell({ row }) {
                              return row.closureOutcome ? (
                                  <StatusBadge module="claimClosureOutcome" status={row.closureOutcome} />
                              ) : (
                                  '-'
                              );
                          },
                          className: 'whitespace-nowrap',
                      }),
                  ]
                : []),
            columnHelper.accessor('policyNo', {
                id: 'policyNo',
                header: 'Policy#',
                sortable: 'policyNo',
                className: 'font-bold',
            }),
            columnHelper.accessor('decision', {
                id: 'decision',
                header: 'Decision',
                sortable: 'decision',
                cell({ row }) {
                    return <ClaimDecisionBadge decision={row.decision} />;
                },
            }),
            columnHelper.accessor((row) => row.PrimaryPolicyHolderContact?.fullName, {
                id: 'fullName',
                header: 'Policy holder',
                cell({ row }) {
                    return (
                        <ContactPopover id={row.PrimaryPolicyHolderContact.id}>
                            {row.PrimaryPolicyHolderContact?.fullName}
                        </ContactPopover>
                    );
                },
            }),
            columnHelper.accessor('causeOfLoss', {
                id: 'causeOfLoss',
                header: 'Cause of loss',
                sortable: 'causeOfLoss',
            }),
            columnHelper.accessor('claimOccurredAt', {
                id: 'dateOfLoss',
                header: 'Date of loss',
                sortable: 'claimOccurredAt',
                cell({ row }) {
                    return formatDate(row.claimOccurredAt);
                },
            }),
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Lodgement date',
                sortable: 'createdAt',
                cell({ row }) {
                    return formatDate(row.createdAt);
                },
            }),
            columnHelper.accessor((row) => row.PrimaryBrokerContact?.fullName, {
                id: 'broker',
                header: 'Broker',
                cell({ row }) {
                    return (
                        <ContactPopover id={row.PrimaryBrokerContact.id}>
                            {row.PrimaryBrokerContact?.fullName}
                        </ContactPopover>
                    );
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Button.OpenInNewTab
                            to="/console/$orgId/claims/details/$id/manage"
                            params={{
                                orgId,
                                id: row.id,
                            }}
                            tooltip="Open in new tab"
                        />
                    );
                },
            }),
        ],
        rowKey(row) {
            return String(row.id);
        },
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            // The Product, Source and Outcome columns follow a feature flag. Merging saved
            // preferences before the flags settle saves an order without them, and they are
            // then appended at the end (as the Training records grid guards, CUR-1064).
            enabled: filterConfigReady,
            persistKey: LIST_TABLE_PERSIST_KEYS.claims,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => listClaimsQuery.refetch(),
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
        },
        showSorterTooltip: false,
        emptyContent:
            listClaimsQuery.isSuccess && listClaimsQuery.data.total === 0 ? (
                <ScopedRegisterEmptyState entity="claims" hasActiveFilters={!isWhereClauseEmpty(queryParams.where)} />
            ) : undefined,
        emptyState: { noRecordsTitle: 'No claims yet' },
    });

    const entityBuiltInViews = useEntityBuiltInViews('owner-ids');
    const builtInViews = useMemo(
        () =>
            deferredBindingEnabled
                ? [
                      ...entityBuiltInViews,
                      {
                          id: AWAITING_PRODUCT_VIEW_ID,
                          name: 'Awaiting product',
                          description: 'Claims recorded before their product was chosen',
                          icon: 'inbox' as const,
                          // The triage queue: awaiting a product and still open. A claim closed without
                          // one (not a claim, a duplicate, withdrawn; CUR-2167) has left it.
                          payload: builtInViewPayload([
                              {
                                  id: 'awaiting-product',
                                  filterId: CLAIM_PRODUCT_FILTER_ID,
                                  operator: 'in',
                                  value: ['PENDING'],
                              },
                              { id: 'awaiting-product-open', filterId: 'status-type', operator: 'in', value: ['OPEN'] },
                          ]),
                      },
                  ]
                : entityBuiltInViews,
        [entityBuiltInViews, deferredBindingEnabled],
    );
    const views = useTableViews({
        entityType: 'CLAIM',
        config: filterConfig,
        configReady: filterConfigReady,
        // Only a view or link that uses a flagged filter waits for the flags; the rest resolve, and
        // fetch, at once (CUR-1036). A view's columns wait for the flagged columns.
        lateFilterIds: CLAIM_DEFERRED_BINDING_FILTER_IDS,
        columnsReady: filterConfigReady,
        urlFilterKey: 'claim-filters',
        builtInViews,
        initialFilters: searchParams['claim-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: 'claims-table-columns',
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />
            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                variant="enhanced"
                actions={
                    <Space size="small">
                        <Button.Selection
                            type="secondary"
                            selectedCount={selectedRowKeys.length}
                            action="Reassign"
                            noun="claims"
                            nounSingular="claim"
                            onClick={() =>
                                setReassignmentAction([
                                    'selected',
                                    (selectedRowKeys as (string | number)[]).map((k) => BigInt(String(k))),
                                ])
                            }
                        />
                        <NewClaimMenu />
                    </Space>
                }
            />

            <ClaimReassignmentDrawer
                action={reassignmentAction}
                onClose={(resetSelectedRowKeys) => {
                    setReassignmentAction(['none']);

                    if (resetSelectedRowKeys) {
                        setSelectedRowKeys([]);
                    }
                }}
            />

            <PrismaTable
                {...tableProps}
                preloadChunk="/console/$orgId/claims/details/$id/manage"
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/claims/details/$id/manage',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
